const data = {
  slug: "ready-to-scale",
  number: "02",
  title: "Ready to Scale",
  intro: [
    "The opportunity is ready. The business may not be. Demand is growing. New markets are opening. The ambition is bigger. But scale creates a different kind of pressure, what worked when the business was smaller can begin to slow decisions, weaken consistency, stretch leadership and multiply complexity.",
    "Scaling is not simply doing more. It is building a business capable of doing more, repeatedly, profitably and without losing control.",
  ],
  sections: [
    {
      kicker: "What it looks like",
      heading: "Growth has created the next problem.",
      body: [
        "A product works. A market has responded. A regional model has gained traction. But the next stage requires more than replicating the same activity at a larger volume. You may be seeing:",
      ],
      items: [
        "Strong performance in one market that is difficult to repeat elsewhere",
        "Growth creating more complexity than capability",
        "Teams making decisions differently across locations or functions",
        "Increasing dependence on the founder or a few senior people",
        "Processes breaking as volumes rise",
        "Sales expanding faster than fulfilment, service or control",
        "New markets being entered without clear prioritisation",
        "Technology and data struggling to support the next stage",
        "Inconsistent customer experience across channels or geographies",
        "Revenue increasing while visibility and operating discipline weaken",
        "More people being added without clearer roles or accountability",
        "Leadership resolving exceptions instead of shaping growth",
      ],
    },
    {
      kicker: "The real problem",
      heading: "What carried the business will not carry the scale.",
      body: [
        "A smaller business can survive on proximity, instinct and individual effort. A larger one cannot. As scale increases, information takes longer to travel, decisions become inconsistent, teams create their own ways of working, customers experience the business differently, margins weaken under complexity, and leadership becomes the bottleneck.",
        "Scale does not create all of these problems. It makes them impossible to ignore.",
      ],
    },
    {
      kicker: "Possible causes",
      heading: "The constraint is rarely ambition alone.",
      items: [
        "The growth model is unclear",
        "Success is not repeatable",
        "The organisation remains founder-dependent",
        "Systems have not caught up",
        "Roles and ownership are unclear",
        "Expansion is creating inconsistency",
        "The economics do not scale",
        "Capability is lagging ambition",
        "Too much is being scaled at once",
      ],
    },
    {
      kicker: "Common mistakes",
      heading: "Scaling too early can be as damaging as scaling too late.",
      items: [
        "Replicate activity before understanding what caused success",
        "Enter too many markets at once",
        "Add people without redesigning the organisation",
        "Install technology before fixing the process",
        "Centralise every decision",
        "Decentralise without clear guardrails",
        "Chase revenue without testing unit economics",
        "Treat every market as the same",
        "Build a scale strategy without an execution model",
      ],
    },
    {
      kicker: "Our approach",
      heading: "How SWITCH assesses scale readiness",
      body: [
        "Before accelerating growth, we assess whether the business can carry it. The diagnostic is shaped around the company and may examine:",
      ],
      items: [
        "Growth ambition and strategic choices",
        "Replicability of the current model",
        "Commercial model and unit economics",
        "Sales and distribution",
        "Technology and data",
        "Operations and fulfilment",
        "Organisation, leadership and governance",
      ],
    },
    {
      kicker: "The founder question",
      heading: "Scale does not mean removing the founder.",
      body: [
        "It means removing the need for the founder to carry the entire business personally. The role evolves from solving every exception, approving every decision and holding every key relationship, toward setting direction, making fewer higher-quality decisions, building leadership capability and allocating capital and attention.",
        "SWITCH helps make that transition without stripping the business of the judgement, speed and character that made it successful.",
      ],
    },
    {
      kicker: "Potential interventions",
      heading: "What we might make scalable",
      body: ["The intervention depends on what must become scalable."],
      items: [
        "Defining the scale thesis",
        "Building a market-expansion roadmap",
        "Standardising what must be repeatable",
        "Preserving what must remain adaptable",
        "Redesigning the organisation",
        "Reducing founder dependency",
        "Strengthening sales and distribution",
        "Upgrading technology and data",
        "Improving operating economics",
        "Establishing a growth operating system",
      ],
    },
    {
      kicker: "From diagnosis to execution",
      heading: "We stay to make it happen",
      body: [
        "A scale strategy is useful only when the organisation can act on it. SWITCH works alongside leadership, internal teams and existing partners, translating the strategy into functional plans, aligning functions, redesigning roles, establishing operating standards and correcting the model as real market evidence emerges, until the agreed priorities are implemented.",
      ],
    },
  ],
};

export default data;
